import React, { Suspense, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Fab } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import Layout from './components/Layout/Layout';
import MobileBottomNav from './components/Mobile/MobileBottomNav';
import { useAppContext } from './context/AppContext';
import { useMobileDetect } from './hooks/useMobileDetect';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';

// Lazy load pages for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const PostCreator = React.lazy(() => import('./pages/PostCreator/PostCreator'));
const PhotoManager = React.lazy(() => import('./pages/PhotoManager/PhotoManager'));
const Calendar = React.lazy(() => import('./pages/Calendar/Calendar'));
const Reviews = React.lazy(() => import('./pages/Reviews/Reviews'));
const Analytics = React.lazy(() => import('./pages/Analytics/Analytics'));
const Settings = React.lazy(() => import('./pages/Settings/Settings'));
const AdminPanel = React.lazy(() => import('./pages/Admin/AdminPanel'));

// Mobile-optimized loading component
const LoadingFallback = () => {
  const { isMobile } = useMobileDetect();
  
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight={isMobile ? "200px" : "400px"}
      flexDirection="column"
      gap={2}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <CircularProgress size={isMobile ? 32 : 40} />
      </motion.div>
      {isMobile && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            Loading...
          </Box>
        </motion.div>
      )}
    </Box>
  );
};

function App() {
  const { isLoading, error, unreadCount } = useAppContext();
  const { 
    isMobile, 
    shouldUseMobileLayout, 
    shouldUseBottomNavigation 
  } = useMobileDetect();
  const navigate = useNavigate();
  
  // Mobile-specific state
  const [showFab, setShowFab] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Handle scroll-based FAB visibility (mobile)
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrollingDown = currentScrollY > lastScrollY;
      
      // Hide FAB when scrolling down, show when scrolling up
      setShowFab(!isScrollingDown || currentScrollY < 100);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isMobile]);

  // Mock notifications for mobile nav
  const notifications = {
    dashboard: 0,
    photos: 0,
    calendar: 2,
    reviews: unreadCount || 5,
    analytics: 0,
  };

  // Show global loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  // Show global error state
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        p={3}
      >
        <ErrorBoundary error={error} />
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: shouldUseBottomNavigation() ? '80px' : 0,
        }}
      >
        <Layout isMobile={shouldUseMobileLayout()}>
          <Suspense fallback={<LoadingFallback />}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Routes>
                {/* Default route redirects to dashboard */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                
                {/* Main application routes */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/posts/create" element={<PostCreator />} />
                <Route path="/posts/edit/:id" element={<PostCreator />} />
                <Route path="/photos" element={<PhotoManager />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/reviews" element={<Reviews />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/admin" element={<AdminPanel />} />
                
                {/* Catch-all route for 404s */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </motion.div>
          </Suspense>
        </Layout>

        {/* Mobile Bottom Navigation */}
        {shouldUseBottomNavigation() && (
          <MobileBottomNav notifications={notifications} />
        )}

        {/* Mobile Floating Action Button */}
        {isMobile && (
          <AnimatePresence>
            {showFab && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                style={{
                  position: 'fixed',
                  bottom: shouldUseBottomNavigation() ? '100px' : '24px',
                  right: '16px',
                  zIndex: 1000,
                }}
              >
                <Fab
                  color="primary"
                  onClick={() => navigate('/posts/create')}
                  sx={{
                    background: (theme) => theme.palette.primary.gradient,
                    boxShadow: '0 8px 32px rgba(30, 58, 138, 0.3)',
                    '&:hover': {
                      boxShadow: '0 12px 40px rgba(30, 58, 138, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  <AddIcon />
                </Fab>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </Box>
    </ErrorBoundary>
  );
}

export default App;